import { faApple } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Step, StepConnector, Stepper } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiBuildResponseBody,
  IApiPipelineResponseBody,
} from '~/lib/api/modules/Builds';

export type IPipelineStepperProps = IContainerProps & {
  build: IApiBuildResponseBody;
  pipeline: IApiPipelineResponseBody;
};

export const PipelineStepper: React.FC<IPipelineStepperProps> = (props) => {
  const { sx, build, pipeline } = props;
  const { t } = useTranslation();
  const currentStepIndex = pipeline.pipeline.findIndex(
    (step) => step.step === pipeline.current.step,
  );
  const stepCount = pipeline.pipeline.length;
  const built =
    currentStepIndex === stepCount - 1 && pipeline.current.status === 'success';
  const activeStep = built
    ? 2
    : pipeline.current.tags?.includes('queue')
      ? 0
      : pipeline.current.tags?.includes('setup') ||
          pipeline.current.tags?.includes('build')
        ? 1
        : undefined;
  const isCurrentPipeline =
    build.pipeline?._id && build.pipeline?._id === pipeline._id;

  return (
    <Stepper
      sx={sx}
      activeStep={activeStep}
      loading={pipeline.current.status === 'running'}
    >
      <Step label={t('PipelineStepper.queued')} />
      <Step
        label={
          activeStep !== undefined && activeStep > 1
            ? t('PipelineStepper.built')
            : t('PipelineStepper.building', {
                pct: String(Math.round((pipeline?.progression ?? 0) * 100)),
              })
        }
        hasError={
          pipeline.current.status === 'error' &&
          (pipeline.current.tags?.includes('setup') ||
            pipeline.current.tags?.includes('build'))
        }
        nextConnector={
          activeStep !== undefined && activeStep > 1 ? (
            <StepConnector></StepConnector>
          ) : undefined
        }
      />
      {build.platform === 'ios'
        ? [
            <Step
              key={0}
              label={t('PipelineStepper.ready-for-review')}
              nextConnector={
                isCurrentPipeline && activeStep === 2 ? (
                  <StepConnector>
                    <Button
                      href={`https://appstoreconnect.apple.com/apps/${build.iosAppId}/distribution/ios/version/inflight`}
                      icon={<FontAwesomeIcon icon={faApple} />}
                      target="_blank"
                    >
                      {t('PipelineStepper.submit-for-review')}
                    </Button>
                  </StepConnector>
                ) : undefined
              }
            />,
            <Step key={1} label={t('PipelineStepper.in-review')} />,
            <Step key={2} label={t('PipelineStepper.reviewed')} />,
            <Step key={3} label={t('PipelineStepper.published')} />,
          ]
        : null}
    </Stepper>
  );
};
